import type { CompanyShellCapabilities } from "@/services/companyShell";
import {
  canAccessEmployeeOnboarding,
} from "@/services/employeeOnboardingCapabilityRules";

export type CompanyShellNavItemDefinition = {
  id: string;
  label: string;
  href: (companyId: string) => string;
  isVisible: (capabilities: CompanyShellCapabilities) => boolean;
};

export type CompanyShellNavGroupDefinition = {
  id: string;
  label: string;
  isVisible: (capabilities: CompanyShellCapabilities) => boolean;
  items: CompanyShellNavItemDefinition[];
};

export type ResolvedCompanyShellNavItem = {
  id: string;
  label: string;
  to: string;
};

export type ResolvedCompanyShellNavGroup = {
  id: string;
  label: string;
  items: ResolvedCompanyShellNavItem[];
};

export const companyShellNavigation: CompanyShellNavGroupDefinition[] = [
  {
    id: "workspace",
    label: "Workspace",
    isVisible: (cap) =>
      cap.can_view_my_workspace ||
      cap.can_view_my_payslips ||
      cap.can_view_my_timesheets ||
      cap.can_view_my_disputes ||
      cap.can_report_my_absences ||
      cap.can_request_my_leave,
    items: [
      {
        id: "my-workspace",
        label: "My Workspace",
        href: (companyId) => `/owner/company/${companyId}/payroll/my`,
        isVisible: (cap) => cap.can_view_my_workspace,
      },
      {
        id: "my-payslips",
        label: "My Payslips",
        href: (companyId) => `/owner/company/${companyId}/payroll/my/payslips`,
        isVisible: (cap) => cap.can_view_my_payslips,
      },
      {
        id: "my-timesheets",
        label: "My Timesheets",
        href: (companyId) => `/owner/company/${companyId}/payroll/my/timesheets`,
        isVisible: (cap) => cap.can_view_my_timesheets,
      },
      {
        id: "my-disputes",
        label: "My Disputes",
        href: (companyId) => `/owner/company/${companyId}/payroll/my/disputes`,
        isVisible: (cap) =>
          cap.can_view_my_disputes ||
          cap.can_submit_my_disputes,
      },
      {
        id: "my-absences",
        label: "My Absences",
        href: (companyId) => `/owner/company/${companyId}/payroll/my/absences`,
        isVisible: (cap) => cap.can_report_my_absences,
      },
      {
        id: "my-leave",
        label: "My Leave",
        href: (companyId) => `/owner/company/${companyId}/payroll/my/leave`,
        isVisible: (cap) => cap.can_request_my_leave,
      },
    ],
  },
  {
    id: "company",
    label: "Company",
    isVisible: (cap) =>
      cap.can_view_company_overview ||
      cap.can_invite_employees ||
      cap.can_manage_employees ||
      cap.can_manage_time_away_opening_balances ||
      cap.can_manage_company_settings ||
      cap.can_manage_company_rule_proposals,
    items: [
      {
        id: "company-overview",
        label: "Overview",
        href: (companyId) => `/owner/company/${companyId}`,
        isVisible: (cap) => cap.can_view_company_overview,
      },
      {
        id: "company-settings",
        label: "Settings",
        href: (companyId) => `/owner/company/${companyId}/settings`,
        isVisible: (cap) => cap.can_manage_company_settings,
      },
      {
        id: "company-workforce-onboarding",
        label: "Workforce Onboarding",
        href: (companyId) => `/owner/company/${companyId}/invite-employee`,
        isVisible: (cap) =>
          canAccessEmployeeOnboarding({
            shellCapabilities: cap,
            importCapabilities: null,
          }),
      },
      {
        id: "company-manage-employees",
        label: "Manage Employees",
        href: (companyId) => `/owner/company/${companyId}/employees`,
        isVisible: (cap) => cap.can_manage_employees,
      },
      {
        id: "company-rules",
        label: "Company Rules",
        href: (companyId) =>
          `/owner/company/${companyId}/company-rules`,
        isVisible: (cap) =>
          cap.can_manage_company_rule_proposals,
      },
      {
        id: "company-import-time-away-balances",
        label: "Import Time-Away Balances",
        href: (companyId) =>
          `/owner/company/${companyId}/employees/import-balances`,
        isVisible: (cap) =>
          cap.can_manage_time_away_opening_balances,
      },
    ],
  },
  {
    id: "payroll",
    label: "Payroll",
    isVisible: (cap) =>
      cap.can_submit_payroll ||
      cap.can_issue_payroll ||
      cap.can_manage_payroll_periods,
    items: [
      {
        id: "payroll-home",
        label: "Home",
        href: (companyId) => `/owner/company/${companyId}/payroll`,
        isVisible: (cap) =>
          cap.can_submit_payroll ||
          cap.can_issue_payroll ||
          cap.can_manage_payroll_periods,
      },
      {
        id: "payroll-timesheets",
        label: "Timesheets",
        href: (companyId) => `/owner/company/${companyId}/payroll/timesheets`,
        isVisible: (cap) => cap.can_submit_payroll,
      },
      {
        id: "payroll-preflight",
        label: "Preflight",
        href: (companyId) => `/owner/company/${companyId}/payroll/preflight`,
        isVisible: (cap) => cap.can_submit_payroll,
      },
      {
        id: "payroll-submit",
        label: "Submit Payroll",
        href: (companyId) => `/owner/company/${companyId}/payroll/submit`,
        isVisible: (cap) => cap.can_submit_payroll,
      },
      {
        id: "payroll-periods-new",
        label: "Off-Schedule Period",
        href: (companyId) =>
          `/owner/company/${companyId}/payroll/periods/new`,
        isVisible: (cap) => cap.can_manage_payroll_periods,
      },
      {
        id: "payroll-issuer-queue",
        label: "Issuer Queue",
        href: (companyId) => `/owner/company/${companyId}/payroll/issuer`,
        isVisible: (cap) => cap.can_issue_payroll,
      },
    ],
  },
];

export function buildVisibleCompanyShellNavigation(
  companyId: string,
  capabilities: CompanyShellCapabilities
): ResolvedCompanyShellNavGroup[] {
  return companyShellNavigation
    .filter((group) => group.isVisible(capabilities))
    .map((group) => ({
      id: group.id,
      label: group.label,
      items: group.items
        .filter((item) => item.isVisible(capabilities))
        .map((item) => ({
          id: item.id,
          label: item.label,
          to: item.href(companyId),
        })),
    }))
    .filter((group) => group.items.length > 0);
}